import { Chip, CircularProgress, Theme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import InfoIcon from '@material-ui/icons/Info';
import LinkIcon from '@material-ui/icons/Link';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import * as R from 'ramda';
import React, { useCallback, useState } from 'react';
import { InactiveMatchReason, JobMatchStatus, SkillType } from '../../API';
import { notEmpty } from '../../lib/functional';
import { createOrUpdateJobMatch, JobMatch } from '../../models/job-matches';
import { ROLE_HUMAN_NAMES } from '../../models/jobs';
import TechnicalSkill from '../../models/technical-skills';
import { Maybe } from '../../types';
import TechnicalSkillChips from '../TechnicalSkillChips';

type StatusInfoWithDescription = {
  label: string;
  description: string;
  color: string;
  background: string;
};

type StringPropsOf<T> = { [K in keyof T]-?: T[K] extends Maybe<string> ? K : never }[keyof T];

type StatusInfoWithSubDescriptions =
  StatusInfoWithDescription
  & { subDescriptions: Record<string, string>, subDescriptionSelector: StringPropsOf<JobMatch> };

function isStatusInfoWithSubDescriptions(
  obj: StatusInfoWithDescription | StatusInfoWithSubDescriptions,
): obj is StatusInfoWithSubDescriptions {
  return 'subDescriptions' in obj;
}

type StatusInfoType =
  Record<JobMatchStatus, StatusInfoWithDescription | StatusInfoWithSubDescriptions>;

const EMAIL = 'engineers-jobs@matildaexp.com';

const STATUS_INFO: StatusInfoType = {
  [JobMatchStatus.IN_REVIEW]: {
    label: 'In Review',
    description: 'We are analysing if this position is a good match for you',
    color: '#fff',
    background: '#c51162',
  },
  [JobMatchStatus.WAITING_FOR_DIAGNOSTIC]: {
    label: 'Technical Evaluation',
    description: 'We are checking if further technical evaluation is required and coordinating with you if so',
    color: '#fff',
    background: '#9c27b0',
  },
  [JobMatchStatus.COMPANY_CHECK]: {
    label: 'Company confirmation',
    description: 'We are checking with the company to confirm your profile fits the role',
    color: '#fff',
    background: '#673ab7',
  },
  [JobMatchStatus.INTERVIEWING]: {
    label: 'Interviewing',
    description: 'Interview process at the company started. All the best! Let us know if we can be of help during the process',
    color: '#fff',
    background: '#1976d2',
  },
  [JobMatchStatus.OFFER]: {
    label: 'Offer',
    description: 'Interview process approved, congrats! Waiting for the offer from the company',
    color: '#fff',
    background: '#00796b',
  },
  [JobMatchStatus.HIRED]: {
    label: 'Hired',
    description: 'Congratulations on your new job! 🎉',
    color: '#fff',
    background: '#00c853',
  },
  [JobMatchStatus.NO_GO]: {
    label: 'Inactive',
    description: `This position did not end up being a good match for you. If you want us to go deeper into why, feel free to email us at ${EMAIL}.`,
    subDescriptions: {
      [InactiveMatchReason.REVIEW]: `After analysing your profile for this position, it doesn’t seem this one will be a good match for you. If you want us to go deeper into why, feel free to email us at ${EMAIL}.`,
      [InactiveMatchReason.DIAGNOSTIC]: `According to our technical evaluation, this position won’t be a good match for you. If you want us to go deeper into why, feel free to email us at ${EMAIL}.`,
      [InactiveMatchReason.COMPANY_REVIEW]: `The company determined your profile is not what they are looking for this position. If you want us to go deeper into why, feel free to email us at ${EMAIL}.`,
      [InactiveMatchReason.COMPANY_INTERVIEWS]: `The interview process at the company didn’t go according to the expectations. If you want us to go deeper into why, feel free to email us at ${EMAIL}.`,
      [InactiveMatchReason.ENGINEER_WITHDRAWAL]: 'You chose not to continue pursuing this position',
      [InactiveMatchReason.COMPANY_INACTIVE]: 'The company closed this position. We will notify you if the same or similar position gets open.',
    },
    subDescriptionSelector: 'inactiveMatchReason',
    color: '#fff',
    background: '#757575',
  },
};

const useStyles = makeStyles<Theme, { interested: boolean }>((theme) => ({
  positionContainer: {
    position: 'relative',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    minHeight: theme.spacing(20),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    opacity: ({ interested }) => (interested ? 1 : 0.5),
  },
  logoContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  companyLogo: {
    width: '100%',
  },
  companyDescription: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& svg': {
      verticalAlign: 'middle',
      marginLeft: theme.spacing(1),
    },
  },
  matchStatus: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardChips: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  menuIcon: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  loadingIndicator: {
    marginRight: 10,
  },
  tooltip: {
    fontSize: theme.typography.body1.fontSize,
  },
}));

type Props = {
  match: JobMatch,
  skills?: TechnicalSkill[],
};

export default function ActiveMatchCard({ match, skills }: Props) {
  const { company } = match.openPosition;
  const [anchorEl, setAnchorEl] = useState(null);
  const [changingInterest, setChangingInterest] = useState(false);
  const [interested, setInterested] = useState(match.engineerInterest ?? true);
  const classes = useStyles({ interested });

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeInterest = async (interest: boolean) => {
    try {
      setChangingInterest(true);
      const jobMatch = await createOrUpdateJobMatch({
        openPositionId: match.openPositionId,
        interest,
      });
      setInterested(jobMatch?.engineerInterest ?? true);
    } catch (err) {
      // no-op
    } finally {
      setChangingInterest(false);
    }
    handleClose();
  };

  const positionSkills = R.map(
    (id) => R.find(R.whereEq({ id, type: SkillType.MAIN }), skills || []),
    match.openPosition.skillIds,
  ).filter(notEmpty);

  const statusInfo = match.status ? STATUS_INFO[match.status] : STATUS_INFO.IN_REVIEW;
  let statusDescription = statusInfo.description;
  if (isStatusInfoWithSubDescriptions(statusInfo)) {
    const subDescKey = match[statusInfo.subDescriptionSelector];
    if (subDescKey && statusInfo.subDescriptions[subDescKey]) {
      statusDescription = statusInfo.subDescriptions[subDescKey];
    }
  }
  return (
    <Paper key={match.id} className={classes.positionContainer}>
      <Grid container spacing={3}>
        <Grid item xs={2} className={classes.logoContainer}>
          <img
            className={classes.companyLogo}
            src={company.logoUrl ? `https://matilda-storage-${(process.env.REACT_APP_ENV === 'production') ? 'prod' : 'dev'}.s3.amazonaws.com/companies/${company.id}.${company.logoUrl}` : undefined}
            alt="Company logo"
          />
        </Grid>
        <Grid item xs={8} className={classes.companyDescription}>
          <Typography variant="h5">
            <Link href={company.url || undefined} target="_blank">
              {company.name}
              <LinkIcon />
            </Link>
          </Typography>
          <Typography variant="h6">{match.openPosition.name}</Typography>
          <Typography variant="body2">{company.summary}</Typography>
          <Box className={classes.cardChips}>
            <Chip label={ROLE_HUMAN_NAMES[match.openPosition.role]} size="small" />
            <TechnicalSkillChips
              skills={positionSkills}
              showFrequency
              small
              noContainer
            />
          </Box>
        </Grid>
        <Grid item xs={2} className={classes.matchStatus}>
          {statusInfo && (
            <Tooltip
              arrow
              interactive
              title={<span dangerouslySetInnerHTML={{ __html: statusDescription }} />}
              classes={{ tooltip: classes.tooltip }}
            >
              <Chip
                label={<strong>{statusInfo.label}</strong>}
                icon={<InfoIcon color="secondary" />}
                color="primary"
                style={{
                  background: statusInfo.background,
                  color: statusInfo.color,
                }}
              />
            </Tooltip>
          )}
        </Grid>
        <div className={classes.menuIcon}>
          <IconButton aria-label="menu" onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem
              onClick={() => handleChangeInterest(!interested)}
              disabled={changingInterest}
            >
              {changingInterest && <CircularProgress color="inherit" size={16} className={classes.loadingIndicator} />}
              {interested ? 'Remove' : 'Add'}
              {' '}
              my interest
            </MenuItem>
          </Menu>
        </div>
      </Grid>
    </Paper>
  );
}
